// RunOverScene — Phase-2 terminal scene (Wave 3 / D-FL-01).
//
// Read-only consumer of RunState (D-LC-02). Composes the 1200×630 share
// card once on create() via ShareCardSystem (D-RT-04 — cached for the
// scene lifetime), displays a ~280×147 downscaled preview (D-RT-03),
// wires keyboard + click handlers for [C] copy text / [T] tweet / [M] menu
// (D-FL-04 — keybind ↔ click parity).
//
// PITFALLS guarded:
//   #3 Clipboard user-gesture: the [T] handler is SYNCHRONOUS up to the
//      ShareCardSystem.copyImageToClipboard call. window.open is invoked
//      BEFORE awaiting the clipboard write (PITFALL #5 popup-blocker).
//   #6 addBase64 key collision: preview texture key is suffixed with
//      `Date.now()` so re-entering RunOverScene from MenuScene doesn't
//      hit a cached old texture.
//   #1 Canvas-taint: if compose() throws or returns null-blob, render
//      "Share card unavailable — please reload" in #ff5566 and hide [T].
//
// captureForTest() returns the cached dataURL — consumed by TestSeam
// (Plan 02-3 Task 3.3) for Plan 02-4's static og-card.png seeding.

import Phaser from 'phaser';
import { TestSeam } from '../debug/TestSeam';
import { RunState } from '../run/RunState';
import { Rng } from '../utils/random';
import { ShareCardSystem, type ShareCardOutput } from '../systems/ShareCardSystem';
import { formatShareSummary, isPremiumHand } from '../run/shareSummary';
import { resolveShareBaseUrl } from '../data/share';

// Layout constants (UI-SPEC §"Visual Layout — RunOverScene (320×240)").
// Visual constants co-located per CashierScene precedent.
const TITLE_Y = 24;
const TITLE_PX = 14;
const PREVIEW_X = 160; // viewport center
const PREVIEW_Y = 113; // (40 + 187) / 2 — block-center per UI-SPEC
const PREVIEW_W = 280;
const PREVIEW_H = 147;
const HAND_LINE_Y = 200;
const HAND_LINE_PX = 12;
const BUTTON_ROW_Y = 220;
const BUTTON_PX = 10;
const TOAST_Y = 220;
const TOAST_PX = 8;
const TOAST_HOLD_MS = 3000;
const TOAST_FADE_MS = 300;
const COLOR_PRIMARY = '#f5e1c8';
const COLOR_PREMIUM = '#f5c542';
const COLOR_ERROR = '#ff5566';

export class RunOverScene extends Phaser.Scene {
  private shareOutput: ShareCardOutput | null = null;
  private emojiSummary = '';
  private toast: Phaser.GameObjects.Text | null = null;
  private tweetButton: Phaser.GameObjects.Text | null = null;
  private hasError = false;

  constructor() {
    super('RunOverScene');
  }

  create(): void {
    TestSeam.setScene('RunOverScene');

    // Title — always rendered, regardless of cashier state.
    this.add
      .text(this.cameras.main.width / 2, TITLE_Y, 'RUN OVER', {
        fontFamily: 'monospace',
        fontSize: `${TITLE_PX}px`,
        color: COLOR_PRIMARY,
      })
      .setOrigin(0.5);

    // Null-cashier guard (UI-SPEC §"Empty state heading"; PATTERNS Fail-Loud-and-Recoverable).
    if (!RunState.cashier) {
      this.add
        .text(
          this.cameras.main.width / 2,
          this.cameras.main.height / 2,
          'RUN OVER: missing cashier result',
          {
            fontFamily: 'monospace',
            fontSize: `${HAND_LINE_PX}px`,
            color: COLOR_ERROR,
          },
        )
        .setOrigin(0.5);
      this.input.keyboard?.on('keydown-M', () => this.scene.start('MenuScene'));
      this.input.keyboard?.on('keydown-ENTER', () => this.scene.start('MenuScene'));
      this.input.keyboard?.on('keydown-SPACE', () => this.scene.start('MenuScene'));
      return;
    }

    // Compute the emoji summary synchronously (Ring 0 — no async surface).
    this.emojiSummary = formatShareSummary(RunState.cashier, Rng.seed, resolveShareBaseUrl());

    // Render hand-line below preview (UI-SPEC §Visual Layout y=200).
    const handColor = isPremiumHand(RunState.cashier.handName) ? COLOR_PREMIUM : COLOR_PRIMARY;
    const handLine = `${RunState.cashier.handName.toUpperCase()} · ${RunState.cashier.score.toLocaleString('en-US')} chips`;
    this.add
      .text(this.cameras.main.width / 2, HAND_LINE_Y, handLine, {
        fontFamily: 'monospace',
        fontSize: `${HAND_LINE_PX}px`,
        color: handColor,
      })
      .setOrigin(0.5);

    // Footer hint (UI-SPEC §Copywriting "footer hint").
    this.add
      .text(this.cameras.main.width / 2, 232, 'press [C] [T] or [M]', {
        fontFamily: 'monospace',
        fontSize: '8px',
        color: '#888888',
      })
      .setOrigin(0.5);

    // Render the button row (UI-SPEC §Visual Layout y=220 — three labels with click parity).
    this.renderButtonRow();

    // Wire keyboard handlers (UI-SPEC §Interaction Contract).
    this.input.keyboard?.on('keydown-M', () => this.handleMenuExit());
    this.input.keyboard?.on('keydown-ENTER', () => this.handleMenuExit());
    this.input.keyboard?.on('keydown-SPACE', () => this.handleMenuExit());
    this.input.keyboard?.on('keydown-C', () => {
      void this.handleCopy();
    });
    // [T] is the load-bearing handler — see PITFALL #3 / #5 ordering inside.
    this.input.keyboard?.on('keydown-T', () => this.handleTweet());

    // Compose the share card asynchronously. The scene shows an empty
    // preview-frame placeholder until the blob lands; on success we
    // load the dataURL via addBase64 and display the preview Image.
    // On failure we render the error UI and hide [T].
    void this.composeAndPreview();
  }

  private async composeAndPreview(): Promise<void> {
    if (!RunState.cashier) return;
    try {
      const output = await ShareCardSystem.compose(this, RunState.cashier, Rng.seed);
      if (!output || !output.blob) {
        this.renderErrorState();
        return;
      }
      this.shareOutput = output;
      // Load the dataURL into Phaser's texture manager. Use a unique key
      // per scene-create so re-entering doesn't collide (PITFALL #6).
      const previewKey = `share-preview-${Date.now()}`;
      this.textures.once(`addtexture-${previewKey}`, () => {
        const preview = this.add.image(PREVIEW_X, PREVIEW_Y, previewKey);
        preview.setDisplaySize(PREVIEW_W, PREVIEW_H);
      });
      this.textures.addBase64(previewKey, output.dataURL);
    } catch (err) {
      // SecurityError, etc. (PITFALL #1 audience-fatal canvas-taint).
      // eslint-disable-next-line no-console
      console.error('[RunOverScene] share-card compose failed:', err);
      this.renderErrorState();
    }
  }

  private renderErrorState(): void {
    this.hasError = true;
    // Replace the preview area with the error message.
    this.add
      .text(this.cameras.main.width / 2, PREVIEW_Y, 'Share card unavailable — please reload', {
        fontFamily: 'monospace',
        fontSize: `${HAND_LINE_PX}px`,
        color: COLOR_ERROR,
      })
      .setOrigin(0.5);
    // Hide the [T] button (UI-SPEC §Error state — "the [T] button is hidden, not disabled").
    this.tweetButton?.setVisible(false);
  }

  private renderButtonRow(): void {
    // Three labels horizontally — each is a separate Text object so
    // setInteractive + pointerdown can wire individual click handlers
    // (D-FL-04 click parity with the keyboard bindings).
    const labelC = this.add
      .text(38, BUTTON_ROW_Y, '[C] Copy summary', {
        fontFamily: 'monospace',
        fontSize: `${BUTTON_PX}px`,
        color: COLOR_PRIMARY,
      })
      .setOrigin(0, 0.5);
    const labelT = this.add
      .text(140, BUTTON_ROW_Y, '[T] Tweet this run', {
        fontFamily: 'monospace',
        fontSize: `${BUTTON_PX}px`,
        color: COLOR_PRIMARY,
      })
      .setOrigin(0, 0.5);
    const labelM = this.add
      .text(245, BUTTON_ROW_Y, '[M] Back to Menu', {
        fontFamily: 'monospace',
        fontSize: `${BUTTON_PX}px`,
        color: COLOR_PRIMARY,
      })
      .setOrigin(0, 0.5);

    this.tweetButton = labelT;

    const wireClick = (
      label: Phaser.GameObjects.Text,
      handler: () => void | Promise<void>,
    ): void => {
      // Hit area inflated 4px (UI-SPEC §Click-target hit area).
      label.setInteractive({ useHandCursor: true });
      label.on('pointerover', () => label.setColor(COLOR_PREMIUM));
      label.on('pointerout', () => label.setColor(COLOR_PRIMARY));
      // pointerdown (NOT pointerup) so the click handler still has user
      // activation when the async clipboard.write fires (PITFALL #3).
      // Surface async rejection via .catch instead of `void`-swallowing.
      label.on('pointerdown', () => {
        const result = handler();
        if (result instanceof Promise) {
          result.catch((err) => console.warn('[wireClick]', err));
        }
      });
    };

    wireClick(labelC, () => this.handleCopy());
    wireClick(labelT, () => this.handleTweet());
    wireClick(labelM, () => this.handleMenuExit());
  }

  private handleMenuExit(): void {
    this.scene.start('MenuScene');
  }

  private async handleCopy(): Promise<void> {
    const ok = await ShareCardSystem.copyTextToClipboard(this.emojiSummary);
    this.showToast(ok ? 'Copied!' : 'Copy failed — try [T] for image');
  }

  private handleTweet(): void {
    // Synchronous-up-to-write ordering (PITFALL #3) + window.open BEFORE
    // await on clipboard (PITFALL #5).
    if (this.hasError || !this.shareOutput) {
      // Should be unreachable — [T] is hidden when hasError; defensive.
      return;
    }
    const blob = this.shareOutput.blob;
    // Fire intent FIRST while we still have transient activation.
    ShareCardSystem.openTwitterIntent(this.emojiSummary);
    // Then attempt clipboard. If this succeeds, the user pastes (⌘V/Ctrl+V) into
    // the already-open compose window. If it fails, fallback to download.
    void ShareCardSystem.copyImageToClipboard(blob).then((ok) => {
      if (ok) {
        this.showToast('Image copied — paste into the tweet (⌘V / Ctrl+V)');
      } else {
        ShareCardSystem.downloadFallback(blob);
        this.showToast('Image saved — attach it to your tweet');
      }
    });
  }

  private showToast(text: string): void {
    // Destroy any in-flight toast first to avoid stacking.
    this.toast?.destroy();
    this.toast = this.add
      .text(this.cameras.main.width / 2, TOAST_Y, text, {
        fontFamily: 'monospace',
        fontSize: `${TOAST_PX}px`,
        color: COLOR_PRIMARY,
      })
      .setOrigin(0.5);
    this.tweens.add({
      targets: this.toast,
      alpha: 0,
      duration: TOAST_FADE_MS,
      delay: TOAST_HOLD_MS,
      onComplete: () => {
        this.toast?.destroy();
        this.toast = null;
      },
    });
  }

  /** TestSeam consumer — returns the cached dataURL or '' if compose hasn't resolved. */
  public captureForTest(): Promise<string> {
    return new Promise((resolve) => {
      if (this.shareOutput) {
        resolve(this.shareOutput.dataURL);
        return;
      }
      // Poll briefly — compose() resolves asynchronously after scene-create.
      // The TestSeam can call this immediately after scene-start.
      const tick = (): void => {
        if (this.shareOutput) resolve(this.shareOutput.dataURL);
        else if (this.hasError) resolve('');
        else this.time.delayedCall(50, tick);
      };
      tick();
    });
  }

  /** TestSeam consumer — public read of the emoji summary for state.runOver field. */
  public getEmojiSummaryForTest(): string {
    return this.emojiSummary;
  }

  /** TestSeam consumer — public read of the share-card readiness for state.runOver field. */
  public hasShareCardForTest(): boolean {
    return this.shareOutput !== null;
  }
}
